import React from "react";
import { BuildingOfficeIcon, DocumentTextIcon, ShieldCheckIcon, UserIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";

const APP_PRIMARY = "var(--app-primary-color, #26256b)";

const ROLES = [
  {
    key: "admin",
    label: "Admin",
    description: "Full platform access with administrative privileges",
    icon: ShieldCheckIcon,
    extra: {},
  },
  {
    key: "customer_admin",
    label: "Customer Admin",
    description: "Manages company settings and users for their organization",
    icon: BuildingOfficeIcon,
    extra: {},
  },
  {
    key: "auditor",
    label: "Auditor",
    description: "Reviews compliance and performs audits",
    icon: DocumentTextIcon,
    extra: { status: "active" },
  },
  {
    key: "security_analyst",
    label: "Security Analyst",
    description: "Monitors security and analyzes threats",
    icon: ShieldCheckIcon,
    extra: { company: "Habilsec", status: "active" },
  },
  {
    key: "end_user",
    label: "End User",
    description: "Standard user with basic access permissions",
    icon: UserIcon,
    extra: {},
  },
];

const RoleCard = ({ role, onSelect }) => {
  const [hovered, setHovered] = React.useState(false);
  return (
    <button
      onClick={() => onSelect(role)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all duration-150 focus:outline-none"
      style={{
        backgroundColor: hovered ? APP_PRIMARY : '#ffffff',
        borderColor: hovered ? APP_PRIMARY : '#f3f4f6',
      }}
    >
      <div
        className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-colors"
        style={{ backgroundColor: hovered ? 'rgba(255,255,255,0.15)' : 'rgba(99,102,241,0.1)' }}
      >
        <role.icon className="w-5 h-5" style={{ color: hovered ? '#ffffff' : APP_PRIMARY }} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold leading-snug transition-colors" style={{ color: hovered ? '#ffffff' : '#111827' }}>
          {role.label}
        </p>
        <p className="text-xs mt-0.5 leading-relaxed transition-colors" style={{ color: hovered ? 'rgba(255,255,255,0.75)' : '#6b7280' }}>
          {role.description}
        </p>
      </div>
    </button>
  );
};

const RoleSelectionModal = ({ setShowRoleSelectionModal, setNewUser, blankUser, setShowCreateUserModal }) => {
  const handleSelect = (role) => {
    setNewUser({ ...blankUser, role: role.key, ...role.extra });
    setShowRoleSelectionModal(false);
    setShowCreateUserModal(true);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.2 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden"
      >
        {/* Header */}
        <div
          className="px-6 py-5 flex items-center justify-between"
          style={{ background: `linear-gradient(135deg, color-mix(in srgb, ${APP_PRIMARY} 90%, black 10%), ${APP_PRIMARY})` }}
        >
          <div>
            <h3 className="text-base font-semibold text-white">Select User Role</h3>
            <p className="text-xs text-white/70 mt-0.5">Choose a role to set permissions for the new user</p>
          </div>
          <button
            onClick={() => setShowRoleSelectionModal(false)}
            className="text-white/70 hover:text-white hover:bg-white/20 rounded-lg p-1.5 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Role cards */}
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ROLES.map((role) => (
            <RoleCard key={role.key} role={role} onSelect={handleSelect} />
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default RoleSelectionModal;
